import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Form } from '@unform/web';
import { FaEnvelope, FaLock, FaCopyright } from 'react-icons/fa';
import * as Yup from 'yup';
import { setDate, setMonth, addMinutes, isWithinInterval } from 'date-fns';

import history from '~/services/history';
import { useAuth } from '~/hooks';
import getValidationErrors from '~/util/getValidationErrors';
import { ButtonSpinner } from '~/components/Spinner';

// Logos temáticos
import defaultLogo from '~/assets/sign-in-logo.svg';
import yellowSeptemberLogo from '~/assets/sign-in-logo-yellow-september.svg';
import pinkOctoberLogo from '~/assets/sign-in-logo-pink-october.svg';
import christmasLogo from '~/assets/sign-in-logo-christmas.svg';

import Input from './Input';
import { Container, Content, AnimationContainer, Footer } from './styles';

const REDIRECT_TO_DESKTOP = false; // Variável para controlar redirecionamento

const MobileSignIn = () => {
  const { signIn } = useAuth();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [color, setColor] = useState('#01579b');

  // Detectar se é desktop e redirecionar se habilitado
  useEffect(() => {
    if (REDIRECT_TO_DESKTOP) {
      const checkDevice = () => {
        const { userAgent } = navigator;
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent
        );
        const isTablet =
          /iPad|Android/i.test(userAgent) && window.innerWidth >= 768;

        if (!isMobile || isTablet) {
          history.push('/signin');
        }
      };

      checkDevice();
      window.addEventListener('resize', checkDevice);

      return () => window.removeEventListener('resize', checkDevice);
    }

    return undefined;
  }, []);

  // Configurar tema sazonal apenas para logo e cor
  useEffect(() => {
    const currentDate = addMinutes(new Date(), 1);
    const logo = document.getElementById('mobile-logo');

    if (!logo) return;

    if (
      isWithinInterval(currentDate, {
        start: setDate(setMonth(new Date(), 8), 1),
        end: setDate(setMonth(new Date(), 8), 30),
      })
    ) {
      // Setembro Amarelo
      logo.setAttribute('src', yellowSeptemberLogo);
      setColor('#f5b342');
    } else if (
      isWithinInterval(currentDate, {
        start: setDate(setMonth(new Date(), 9), 1),
        end: setDate(setMonth(new Date(), 9), 31),
      })
    ) {
      // Outubro Rosa
      logo.setAttribute('src', pinkOctoberLogo);
      setColor('#ff69b4');
    } else if (
      isWithinInterval(currentDate, {
        start: setDate(setMonth(new Date(), 11), 1),
        end: setDate(setMonth(new Date(), 11), 31),
      })
    ) {
      // Natal
      logo.setAttribute('src', christmasLogo);
      setColor('#228b22');
    } else {
      // Padrão
      logo.setAttribute('src', defaultLogo);
      setColor('#01579b');
    }
  }, []);

  const handleSubmit = useCallback(
    async data => {
      try {
        setLoading(true);
        formRef.current.setErrors({});

        const schema = Yup.object().shape({
          email: Yup.string()
            .required('E-mail obrigatório')
            .email('Digite um e-mail válido'),
          password: Yup.string().required('Senha obrigatória'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { email, password } = data;

        // Registrar que o login foi feito pela rota mobile
        localStorage.setItem('@Diretiva:loginSource', 'mobile');

        await signIn({
          email,
          password,
        });

        // Após login bem-sucedido, verificar se há redirecionamento
        const redirectPath = localStorage.getItem(
          '@Diretiva:redirectAfterLogin'
        );
        if (redirectPath && redirectPath.startsWith('/mobile-')) {
          localStorage.removeItem('@Diretiva:redirectAfterLogin');
          history.push(redirectPath);
        } else {
          // Redirecionar para a página de protocolos mobile por padrão
          history.push('/mobile-protocol');
        }

        // O redirecionamento agora é feito no contexto de auth
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current.setErrors(errors);
        }
        // Remover toast aqui pois já é mostrado no contexto
        setLoading(false);
      }
    },
    [signIn]
  );

  return (
    <Container loading={loading ? 1 : 0}>
      <Content>
        <AnimationContainer color={color}>
          <img id="mobile-logo" alt="logo" />

          <Form ref={formRef} onSubmit={handleSubmit}>
            <h1>Faça seu login</h1>

            <Input
              name="email"
              icon={FaEnvelope}
              type="email"
              placeholder="E-mail"
              color={color}
            />

            <Input
              name="password"
              icon={FaLock}
              type="password"
              placeholder="Senha"
              color={color}
            />

            <button type="submit">
              {loading ? <ButtonSpinner size={14} /> : 'Entrar'}
            </button>
          </Form>
        </AnimationContainer>

        <Footer>
          <div>
            <FaCopyright />
            <strong>2020 - Contabilizar Digital</strong>
          </div>
          <span>Todos os direitos reservados</span>
        </Footer>
      </Content>
    </Container>
  );
};

export default MobileSignIn;
